import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

const MAX_RANGE_HOURS = 168; // 7 días

// Presets estilo flespi: clic aplica de inmediato
const PRESETS = [
  { label: 'Hoy', getRange: () => [moment().startOf('day'), moment()] },
  { label: 'Ayer', getRange: () => [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')] },
  { label: 'Últimas 6 horas', getRange: () => [moment().subtract(6, 'hours'), moment()] },
  { label: 'Últimas 24 horas', getRange: () => [moment().subtract(24, 'hours'), moment()] },
  { label: 'Últimos 7 días', getRange: () => [moment().subtract(7, 'days'), moment()] }
];

/**
 * Selector de rango fecha/hora estilo flespi: barra compacta con el rango
 * aplicado y flechas ‹ › que lo desplazan una duración completa, más un
 * popover con presets y rango custom.
 *
 * @param {Function} onDateRangeChange - Callback ({ startDateTime, endDateTime })
 *   con strings 'YYYY-MM-DD HH:mm:ss' en hora local del navegador.
 */
function DateTimeSelector({ onDateRangeChange }) {
  // Rango aplicado (el que está consultado en el mapa)
  const [startDate, setStartDate] = useState(moment().subtract(1, 'hour'));
  const [endDate, setEndDate] = useState(moment());
  // Rango en edición dentro del popover
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [timeoutError, setTimeoutError] = useState(false);
  // Timeout de UI escalado según la duración del rango aplicado
  const timeoutMsRef = useRef(45000);

  const validateDateRange = (start, end) => {
    if (!start || !end) {
      setError('Ambas fechas son requeridas');
      return false;
    }

    if (!start.isValid() || !end.isValid()) {
      setError('Formato de fecha inválido');
      return false;
    }

    if (start.isAfter(end)) {
      setError('La fecha de inicio no puede ser posterior a la fecha final');
      return false;
    }

    if (end.diff(start, 'hours', true) > MAX_RANGE_HOURS) {
      setError('El rango no puede ser mayor a 7 días');
      return false;
    }

    setError(null);
    return true;
  };

  useEffect(() => {
    let timeoutId;

    if (isLoading) {
      // Mostrar error de timeout si la consulta demora demasiado
      timeoutId = setTimeout(() => {
        setTimeoutError(true);
        setIsLoading(false);
      }, timeoutMsRef.current);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading]);

  // Núcleo único: valida, aplica el rango y dispara la consulta
  const applyRange = (start, end) => {
    if (!validateDateRange(start, end)) return;

    setStartDate(start);
    setEndDate(end);
    setAnchorEl(null);
    setIsLoading(true);
    setTimeoutError(false);
    // Rangos largos tardan más en Valhalla: 45s hasta 24h, 120s sobre eso
    timeoutMsRef.current = end.diff(start, 'hours', true) > 24 ? 120000 : 45000;

    const promise = Promise.resolve(
      onDateRangeChange({
        startDateTime: start.format('YYYY-MM-DD HH:mm:ss'),
        endDateTime: end.format('YYYY-MM-DD HH:mm:ss')
      })
    );

    promise
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error applying date range:', err);
        setError('Error al procesar la solicitud');
        setIsLoading(false);
      });
  };

  // Desplaza el rango una duración completa hacia atrás (-1) o adelante (+1)
  const handleShift = (direction) => {
    const durationMs = endDate.diff(startDate);
    applyRange(
      startDate.clone().add(direction * durationMs, 'ms'),
      endDate.clone().add(direction * durationMs, 'ms')
    );
  };

  // Deshabilitar › si el rango desplazado quedaría completamente en el futuro
  const shiftForwardDisabled = isLoading || endDate.isSameOrAfter(moment());

  const openPopover = (event) => {
    setDraftStart(startDate.clone());
    setDraftEnd(endDate.clone());
    setError(null);
    setAnchorEl(event.currentTarget);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ width: '100%', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Rango de fecha y hora
        </Typography>

        {/* Barra compacta: ‹ [rango aplicado] › */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleShift(-1)}
            disabled={isLoading}
            aria-label="Rango anterior"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          <ButtonBase
            onClick={openPopover}
            disabled={isLoading}
            sx={{
              flex: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 1,
              py: 0.75,
              justifyContent: 'center',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            {isLoading ? (
              <CircularProgress size={18} />
            ) : (
              <Typography sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                {startDate.format('DD/MM/YY HH:mm')} – {endDate.format('DD/MM/YY HH:mm')}
              </Typography>
            )}
          </ButtonBase>

          <IconButton
            size="small"
            onClick={() => handleShift(1)}
            disabled={shiftForwardDisabled}
            aria-label="Rango siguiente"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>

        {error && !anchorEl && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {timeoutError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            Tiempo de espera agotado. Intente nuevamente.
          </Typography>
        )}

        {/* Popover: presets + rango custom */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            setError(null); // no arrastrar errores del borrador a la barra
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ width: 300, p: 1 }}>
            <List dense disablePadding>
              {PRESETS.map((preset) => (
                <ListItemButton
                  key={preset.label}
                  onClick={() => applyRange(...preset.getRange())}
                >
                  <ListItemText primary={preset.label} />
                </ListItemButton>
              ))}
            </List>

            <Divider sx={{ my: 1 }} />

            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              Rango personalizado (máx. 7 días)
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
              <DateTimePicker
                label="Fecha y hora inicio"
                value={draftStart}
                onChange={setDraftStart}
                ampm={false}
                format="YYYY-MM-DD HH:mm"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DateTimePicker
                label="Fecha y hora fin"
                value={draftEnd}
                onChange={setDraftEnd}
                ampm={false}
                format="YYYY-MM-DD HH:mm"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />

              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}

              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => applyRange(draftStart, draftEnd)}
              >
                Aplicar
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
}

export default DateTimeSelector;
